import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  isCompanyManagerUser,
  isSuperUserSession,
} from "../../components/content/axiosConfig.js";
import Preloader from "../../components/custom/preLoader";
import showErrorToast from "../../components/toast/toast";
import DashboardStatCard from "../../components/custom/DashboardStatCard";
import DashboardSpeciesStatCard from "../../components/custom/DashboardSpeciesStatCard";
import { useDashboardOwner } from "../../hooks/queries/useDashboardOwner";
import { useDashboardStaff } from "../../hooks/queries/useDashboardStaff";

function useDashboardStats() {
  const user = getUserDetails();
  const scopedFisherId = user?.FisherID;
  const sandboxFisherId =
    user?.sandboxFisherId != null && user.sandboxFisherId !== ""
      ? Number(user.sandboxFisherId)
      : null;
  const companyOrSuper = isCompanyManagerUser(user) || isSuperUserSession(user);
  const fisherId =
    scopedFisherId != null && scopedFisherId !== ""
      ? scopedFisherId
      : companyOrSuper && isCompanyManagerUser(user)
        ? sandboxFisherId
        : scopedFisherId;
  const useStaffScope = !companyOrSuper && (fisherId == null || fisherId === "");

  const ownerQuery = useDashboardOwner(fisherId, {
    enabled: !useStaffScope && fisherId != null && fisherId !== "",
  });
  const staffQuery = useDashboardStaff({ enabled: useStaffScope });

  return useStaffScope ? staffQuery : ownerQuery;
}

const DASHBOARD_STAT_LINKS = {
  ponds: "/pond",
  sampledFish: "/dashboard/sampled-fish",
  fishTypes: "/dashboard/fish-types",
  samplingEvents: "/dashboard/sampling-events",
};

const QUICK_ACTIONS = [
  { path: "/pond", label: "Ponds", icon: "fa-water" },
  { path: "/fishingData", label: "Sampling Data", icon: "fa-fish" },
  { path: "/FishDataTableView", label: "Table View", icon: "fa-table" },
  { path: "/fish-data-graph-new", label: "Graph View", icon: "fa-chart-line" },
  { path: "/staff", label: "Users", icon: "fa-users" },
  { path: "/profile", label: "Profile", icon: "fa-user" },
];

function OwnerBiggestFishSection({ fishList }) {
  if (!fishList?.length) return null;

  return (
    <section className="donezo-dashboard-section">
      <div className="donezo-dashboard-section__header">
        <h2 className="donezo-dashboard-section__title">Biggest fish by species</h2>
      </div>
      <div className="row g-3">
        {fishList.map((fish, index) => {
          const pondLine = [fish.pondName || "—", fish.caughtAt].filter(Boolean).join(" · ");
          return (
            <DashboardSpeciesStatCard
              key={fish.fishTypeId || fish.fishTypeName}
              colorIndex={index}
              title={fish.fishTypeName}
              length={fish.length}
              weight={fish.weight}
              wr={fish.wrLabel || fish.wr}
              footer={pondLine}
            />
          );
        })}
      </div>
    </section>
  );
}

const Dashboard = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { data: stats = {}, isLoading, isError, error } = useDashboardStats();
  const user = getUserDetails();
  const companyUnscopedDemo =
    isCompanyManagerUser(user) &&
    !user?.scopedAccountId &&
    user?.sandboxFisherId != null &&
    user.sandboxFisherId !== "";
  const demoStatsLabel = user?.sandboxFisherName || "your Demo account";
  const speciesBiggestFish = stats?.speciesBiggestFish ?? [];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const CheckExpiry = useCallback(async () => {
    await CheckUserExpiry();
  }, []);

  useEffect(() => {
    if (!isError) return;
    const current = getUserDetails();
    if (
      error?.response?.status === 401 &&
      !isCompanyManagerUser(current) &&
      !isSuperUserSession(current)
    ) {
      window.location.replace("/user/login");
      return;
    }
    showErrorToast(
      error?.response?.data?.message || "Unable to load dashboard data. Please try again."
    );
  }, [isError, error]);

  useEffect(() => {
    checkTokenValidity();
    CheckExpiry();
  }, [CheckExpiry]);

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page donezo-list-page donezo-dashboard-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <div className="donezo-orders-toolbar donezo-dashboard-toolbar">
          <div className="donezo-dashboard-toolbar__intro">
            <h1 className="donezo-orders-toolbar__title">Dashboard</h1>
            <p className="donezo-dashboard-toolbar__subtitle">
              Overview of ponds, sampling activity, and top catches.
            </p>
          </div>
        </div>

        {companyUnscopedDemo ? (
          <div className="donezo-dashboard-banner" role="status">
            <span className="donezo-dashboard-banner__label">Demo</span>
            <span className="donezo-dashboard-banner__text">
              Stats below are for <strong>{demoStatsLabel}</strong>. Open a client account to see
              that organization&apos;s dashboard.
            </span>
          </div>
        ) : null}

        <div className="donezo-orders-panel donezo-dashboard-stats">
          <div className="row g-0">
            <DashboardStatCard
              colClass="col-6 col-xl-3"
              title="Total ponds"
              value={stats?.NumberOfPonds || 0}
              icon="fa-water"
              colorIndex={0}
              to={DASHBOARD_STAT_LINKS.ponds}
            />
            <DashboardStatCard
              colClass="col-6 col-xl-3"
              title="Sampled fish"
              value={stats?.TotalSampledFish ?? stats?.NumberOfFishes ?? 0}
              icon="fa-fish"
              colorIndex={1}
              to={DASHBOARD_STAT_LINKS.sampledFish}
            />
            <DashboardStatCard
              colClass="col-6 col-xl-3"
              title="Fish types"
              value={stats?.NumberOfFishTypes || 0}
              icon="fa-layer-group"
              colorIndex={2}
              to={DASHBOARD_STAT_LINKS.fishTypes}
            />
            <DashboardStatCard
              colClass="col-6 col-xl-3"
              title="Sampling events"
              value={stats?.NumberOfSamplingEvents ?? stats?.NumberOfFishesSampled ?? 0}
              icon="fa-calendar-check"
              colorIndex={3}
              to={DASHBOARD_STAT_LINKS.samplingEvents}
            />
          </div>
        </div>

        <OwnerBiggestFishSection fishList={speciesBiggestFish} />

        {isMobile && (
          <section className="donezo-dashboard-section">
            <div className="donezo-dashboard-section__header">
              <h2 className="donezo-dashboard-section__title">Quick actions</h2>
              <p className="donezo-dashboard-section__subtitle">Jump to common screens.</p>
            </div>
            <div className="donezo-dashboard-actions">
              {QUICK_ACTIONS.map((item) => (
                <Link key={item.path} to={item.path} className="donezo-dashboard-actions__item">
                  <span className="donezo-dashboard-actions__icon" aria-hidden>
                    <i className={`fa ${item.icon}`} />
                  </span>
                  <span className="donezo-dashboard-actions__label">{item.label}</span>
                  <i className="fa fa-chevron-right donezo-dashboard-actions__chevron" aria-hidden />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
